import type { RouteObject } from "react-router-dom";
import { lazy } from "react";
import { addSlashes, withCompanyPrefix } from "../route.utils";
import { CreateProjectPage } from "@/pages/(home)/(company)/projects/create";
import { ProjectDetailsPage } from "@/pages/(home)/(company)/projects/view";
import { AddProjectTeamForm } from "@/pages/(home)/(company)/projects/view/tab-project-team/add-project-team-form";
import { AddKPIForm } from "@/pages/(home)/(company)/projects/view/tab-project-kpi/add-kpi-form";
import { AddOKRForm } from "@/pages/(home)/(company)/projects/view/tab-project-okr/add-okr-form";
import { ProjectAttachmentDetailPage } from "@/pages/(home)/(company)/projects/view/tab-project-attachments/project-attachment-detail";
import { ProjectKPIDetailPage } from "@/pages/(home)/(company)/projects/view/tab-project-kpi/project-kpi-detail";
import { ProjectOKRDetailPage } from "@/pages/(home)/(company)/projects/view/tab-project-okr/project-okr-detail";
import { attachmentIdKey, kpiIdKey, okrIdKey, projectIdKey } from "./routes.projects.constants";
import { store } from "@/store";
import { companyProjectsApi } from "@/services/(company)/api.projects";

// Lazy so the route file doesn't eagerly pull the tabs page (which imports
// nav-tabs.config) during config initialization — avoids a circular-import TDZ.
const CompanyProjectsPage = lazy(() =>
  import("@/pages/(home)/(company)/projects").then((m) => ({
    default: m.CompanyProjectsPage,
  })),
);
const CompanyProjectsListPage = lazy(
  () => import("@/pages/(home)/(company)/projects/list"),
);

export const COMPANY_PROJECTS_PATH_SEGMENTS = {
  ROOT: "projects",
  CREATE: "create",
  VIEW: `:${projectIdKey}`,
  EDIT: "edit",
  ADD_TEAM: "team",
  KPI: {
    ADD: "kpi",
    EDIT: `:${kpiIdKey}`,
  },
  OKR: {
    ADD: "okr",
    EDIT: `:${okrIdKey}`,
  },
  ATTACHMENT_VIEW: `attachments/:${attachmentIdKey}`,
};

export const companyProjectsRoutes: RouteObject = {
  path: COMPANY_PROJECTS_PATH_SEGMENTS.ROOT,
  handle: { breadcrumb: "Projects" },
  children: [
    // ── Page-level tabs layout (single "Projects" tab at the module root) ────
    {
      Component: CompanyProjectsPage,
      handle: { flushContent: true },
      children: [{ index: true, Component: CompanyProjectsListPage }],
    },
    {
      path: COMPANY_PROJECTS_PATH_SEGMENTS.CREATE,
      handle: { breadcrumb: "Create Project" },
      Component: CreateProjectPage,
    },
    {
      path: COMPANY_PROJECTS_PATH_SEGMENTS.VIEW,
      children: [
        {
          index: true,
          Component: ProjectDetailsPage,
          loader: async ({ params }) => {
            const projectId = params[projectIdKey];
            if (!projectId) return { breadcrumbLabel: "Project Details" };
            const result = await store.dispatch(
              companyProjectsApi.endpoints.getProjectDetailsById.initiate({ projectId }),
            );
            const name = result.data?.data?.name ?? `Project #${projectId}`;
            return { breadcrumbLabel: name };
          },
          handle: {
            breadcrumb: (data: { breadcrumbLabel: string }) => data?.breadcrumbLabel ?? "Project Details",
            title: "Project Details",
          },
        },
        {
          path: COMPANY_PROJECTS_PATH_SEGMENTS.EDIT,
          handle: { breadcrumb: "Edit Project" },
          Component: CreateProjectPage,
        },
        {
          path: COMPANY_PROJECTS_PATH_SEGMENTS.ADD_TEAM,
          handle: { breadcrumb: "Add Team" },
          Component: AddProjectTeamForm,
        },
        {
          path: COMPANY_PROJECTS_PATH_SEGMENTS.KPI.ADD,
          handle: { breadcrumb: "KPI" },
          children: [
            {
              index: true,
              Component: AddKPIForm,
            },
            {
              path: COMPANY_PROJECTS_PATH_SEGMENTS.KPI.EDIT,
              children: [
                { index: true, handle: { breadcrumb: "Edit KPI" }, Component: AddKPIForm },
                { path: "view", handle: { breadcrumb: "KPI Detail" }, Component: ProjectKPIDetailPage },
              ],
            },
          ],
        },
        {
          path: COMPANY_PROJECTS_PATH_SEGMENTS.OKR.ADD,
          handle: { breadcrumb: "OKR" },
          children: [
            {
              index: true,
              Component: AddOKRForm,
            },
            {
              path: COMPANY_PROJECTS_PATH_SEGMENTS.OKR.EDIT,
              children: [
                { index: true, handle: { breadcrumb: "Edit OKR" }, Component: AddOKRForm },
                { path: "view", handle: { breadcrumb: "OKR Detail" }, Component: ProjectOKRDetailPage },
              ],
            },
          ],
        },
        {
          path: COMPANY_PROJECTS_PATH_SEGMENTS.ATTACHMENT_VIEW,
          handle: { breadcrumb: "Attachment Detail" },
          Component: ProjectAttachmentDetailPage,
        },
      ],
    },
  ],
};

export const COMPANY_PROJECTS_PATHS = Object.freeze({
  ROOT: withCompanyPrefix(addSlashes(COMPANY_PROJECTS_PATH_SEGMENTS.ROOT)),
  CREATE: withCompanyPrefix(addSlashes(COMPANY_PROJECTS_PATH_SEGMENTS.ROOT, COMPANY_PROJECTS_PATH_SEGMENTS.CREATE)),
  VIEW: (projectId: string | number) =>
    withCompanyPrefix(addSlashes(COMPANY_PROJECTS_PATH_SEGMENTS.ROOT, String(projectId))),
  EDIT: (projectId: string | number) =>
    withCompanyPrefix(
      addSlashes(COMPANY_PROJECTS_PATH_SEGMENTS.ROOT, String(projectId), COMPANY_PROJECTS_PATH_SEGMENTS.EDIT),
    ),
  ADD_TEAM: (projectId: string | number) =>
    withCompanyPrefix(
      addSlashes(COMPANY_PROJECTS_PATH_SEGMENTS.ROOT, String(projectId), COMPANY_PROJECTS_PATH_SEGMENTS.ADD_TEAM),
    ),
  ADD_KPI: (projectId: string | number) =>
    withCompanyPrefix(
      addSlashes(COMPANY_PROJECTS_PATH_SEGMENTS.ROOT, String(projectId), COMPANY_PROJECTS_PATH_SEGMENTS.KPI.ADD),
    ),
  EDIT_KPI: (projectId: string | number, kpiId: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_PROJECTS_PATH_SEGMENTS.ROOT,
        String(projectId),
        COMPANY_PROJECTS_PATH_SEGMENTS.KPI.ADD,
        String(kpiId),
      ),
    ),
  ADD_OKR: (projectId: string | number) =>
    withCompanyPrefix(
      addSlashes(COMPANY_PROJECTS_PATH_SEGMENTS.ROOT, String(projectId), COMPANY_PROJECTS_PATH_SEGMENTS.OKR.ADD),
    ),
  EDIT_OKR: (projectId: string | number, kpiId: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_PROJECTS_PATH_SEGMENTS.ROOT,
        String(projectId),
        COMPANY_PROJECTS_PATH_SEGMENTS.OKR.ADD,
        String(kpiId),
      ),
    ),
  VIEW_ATTACHMENT: (projectId: string | number, attachmentId: string | number) =>
    withCompanyPrefix(
      addSlashes(COMPANY_PROJECTS_PATH_SEGMENTS.ROOT, String(projectId), "attachments", String(attachmentId)),
    ),
  VIEW_KPI: (projectId: string | number, kpiId: string | number) =>
    withCompanyPrefix(
      addSlashes(COMPANY_PROJECTS_PATH_SEGMENTS.ROOT, String(projectId), "kpi", String(kpiId), "view"),
    ),
  VIEW_OKR: (projectId: string | number, okrId: string | number) =>
    withCompanyPrefix(
      addSlashes(COMPANY_PROJECTS_PATH_SEGMENTS.ROOT, String(projectId), "okr", String(okrId), "view"),
    ),
});
